import { useState } from 'react';
import { Plus, MapPin, Pencil, Trash2 } from 'lucide-react';
import { useAddresses, useDeleteAddress } from '../../hooks/queries/useAddresses';
import { AddressModal } from './AddressModal';
import type { Address } from '../../types';

interface AddressListProps {
    onSelect?: (address: Address) => void;
    selectedId?: string;
    selectable?: boolean;
}

export function AddressList({ onSelect, selectedId, selectable = false }: AddressListProps) {
    const { data: addresses, isLoading, error } = useAddresses();
    const deleteAddress = useDeleteAddress();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAddress, setEditingAddress] = useState<Address | undefined>(undefined);

    const handleEdit = (address: Address, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingAddress(address);
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (confirm('¿Estás seguro de eliminar esta dirección?')) {
            await deleteAddress.mutateAsync(id);
        }
    };

    const handleAddNew = () => {
        setEditingAddress(undefined);
        setIsModalOpen(true);
    };

    if (isLoading) return <div className="p-4 text-center text-gray-500">Cargando direcciones...</div>;
    if (error) return <div className="p-4 text-center text-red-500">Error al cargar direcciones</div>;

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="font-semibold text-gray-900 dark:text-white">Mis Direcciones</h3>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Nueva
                </button>
            </div>

            <div className="grid gap-3">
                {addresses?.length === 0 && (
                    <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
                        <MapPin className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">No tenés direcciones guardadas</p>
                        <button
                            onClick={handleAddNew}
                            className="text-sm font-medium text-primary hover:underline"
                        >
                            Agregar mi primera dirección
                        </button>
                    </div>
                )}
                {addresses?.map((address) => (
                    <div
                        key={address.uuid}
                        onClick={() => selectable && onSelect && onSelect(address)}
                        className={`
              relative p-4 rounded-xl border transition-all cursor-pointer
              ${selectable
                                ? (selectedId === address.uuid
                                    ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10 ring-1 ring-yellow-500'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-yellow-500/50')
                                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                            }
            `}
                    >
                        <div className="flex items-start gap-3">
                            <div className={`mt-1 p-2 rounded-full ${selectedId === address.uuid ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                                <MapPin className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                            {address.label}
                                            {address.is_default && <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">Default</span>}
                                        </h4>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                            {address.street} {address.number}
                                            {address.floor ? `, Piso ${address.floor}` : ''}
                                            {address.apartment ? ` ${address.apartment}` : ''}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {address.district}{address.city ? `, ${address.city}` : ''}
                                        </p>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={(e) => handleEdit(address, e)}
                                            className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={(e) => handleDelete(address.uuid, e)}
                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

            </div>

            <AddressModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                addressToEdit={editingAddress}
            />
        </div>
    );
}
